import { styled } from '@linaria/react';
import { Menu } from '@mantine/core';
import type { FloraPage } from '../../../api/flora-pages-client/flora-pages-client.type';
import { FloraPageVisibility } from '../../../api/flora-pages-client/flora-pages-client.type';
import { Icon } from '../../../ui-library/icon/icon';

interface FloraPageActionsMenuProps {
  page: FloraPage;
  onToggleVisibility: (page: FloraPage) => void;
  onRequestDelete: (page: FloraPage) => void;
}

/**
 * Owner-only kebab menu for a Flora page: share/unshare with the organization
 * and delete. Shared by the grid card and the table row so both surfaces stay
 * in sync on the same actions.
 *
 * @param {FloraPageActionsMenuProps} props - The page and its action handlers.
 * @returns {JSX.Element} The kebab menu trigger and dropdown.
 */
export function FloraPageActionsMenu({ page, onToggleVisibility, onRequestDelete }: FloraPageActionsMenuProps) {
  const isShared = page.visibility === FloraPageVisibility.Organization;

  return (
    <Menu position="bottom-end" withinPortal>
      <Menu.Target>
        <KebabButton type="button" aria-label="Page actions" onClick={(event) => event.stopPropagation()}>
          <Icon name="more_vert" size={20} />
        </KebabButton>
      </Menu.Target>
      <Menu.Dropdown onClick={(event) => event.stopPropagation()}>
        <Menu.Item onClick={() => onToggleVisibility(page)}>
          {isShared ? 'Make private' : 'Share with organization'}
        </Menu.Item>
        <Menu.Item color="red" onClick={() => onRequestDelete(page)}>
          Delete
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

const KebabButton = styled.button`
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
`;
